import { useContext } from 'react';

import type { ContactCandidateData } from 'hooks/shared/useContactCandidateMutation/useContactCandidateMutation.types';
import type { Candidate } from 'model/Candidate/Candidate';

import { logger } from 'config/logger';
import { isAxiosError } from 'helpers/clientHelpers';
import { useContactCandidateMutation } from 'hooks/shared/useContactCandidateMutation/useContactCandidateMutation';
import { EventBus } from 'services/event-bus/EventBus';
import { CandidateEvent } from 'services/event-bus/EventBus.types';
import { getDialogKeyByPageContext } from 'shared/legacy/contexts/DialogContext/getDialogKeyByPageContext';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { useCreateSnackbar } from 'shared/legacy/contexts/SnackbarContext/useCreateSnackbar';
import { useCandidateNotesMutation } from 'shared/legacy/hooks/useCandidateNotesMutation';
import { useGlobalDialogsStore } from 'stores/global-dialogs/global-dialogs.store';
import { GlobalDialogType } from 'stores/global-dialogs/global-dialogs.store.types';
import { useGlobalDialog } from 'stores/global-dialogs/hooks/useGlobalDialog';
import { getPageContext } from 'utils/getPageContext';

type SaveProps = {
  note: string;
  candidate: Candidate;
};

export const useSave = ({ note, candidate }: SaveProps) => {
  const { dictionary } = useContext(LocalizationContext);
  const { close } = useGlobalDialog();
  const remove = useGlobalDialogsStore((state) => state.remove);

  const contactCandidateMutation = useContactCandidateMutation();
  const candidateNotesMutation = useCandidateNotesMutation();
  const createSnackbar = useCreateSnackbar();

  const handleSave = async ({
    hasFollowUp = false,
    newLabels = [],
  }: { hasFollowUp?: boolean; newLabels?: string[] } = {}): Promise<{
    hasError: boolean;
  }> => {
    if (
      contactCandidateMutation.isLoading ||
      candidateNotesMutation.isLoading ||
      !candidate.campaignId ||
      !candidate.es_person_id
    )
      return { hasError: false };

    EventBus.startTransaction(candidate.es_person_id, {
      name: candidate.firstName,
      esPersonId: candidate.es_person_id,
      labels: candidate.reasons,
      status: candidate.status,
    });

    try {
      if (note) {
        await candidateNotesMutation.mutateAsync({
          note,
          candidate: {
            es_person_id: candidate.es_person_id,
            campaignId: candidate.campaignId,
            status: candidate.status,
          },
        });
      }

      await contactCandidateMutation.mutateAsync({
        campaignId: candidate.campaignId,
        sendMethod: 'call',
        activeJobBoard: candidate.activeJobBoard,
        candidateStatus: candidate.status,

        data: {
          esPersonId: candidate.es_person_id,
          newLabels,
          previousLabels: candidate.reasons,
        } satisfies ContactCandidateData<'call'>,
      });

      EventBus.emit(
        CandidateEvent.LabelsUpdated,
        { esPersonId: candidate.es_person_id, labels: newLabels },
        candidate.es_person_id,
      );

      if (!hasFollowUp) {
        EventBus.commitTransaction(candidate.es_person_id);
      }
    } catch (e) {
      if (
        isAxiosError<{ status: string }>(e) &&
        e.response &&
        e.response.status === 400 &&
        e.response.data.status === "This candidate was already matched and can't have his status changed"
      ) {
        EventBus.clearTransaction(candidate.es_person_id);

        createSnackbar(dictionary.candidateAlreadyInteracted, { variant: 'danger' });

        return { hasError: true };
      }

      logger.error(e);
      createSnackbar(isAxiosError(e) ? e.message : dictionary.somethingWentWrong, { variant: 'danger' });

      return { hasError: true };
    } finally {
      const key = getDialogKeyByPageContext(
        GlobalDialogType.CampaignPhoneReachOut,
        getPageContext(),
        candidate.campaignId.toString(),
      );

      close(GlobalDialogType.CampaignPhoneReachOut);
      remove(GlobalDialogType.CampaignPhoneReachOut, key);
    }

    return { hasError: false };
  };

  return {
    handleSave,
    isSaving: candidateNotesMutation.isLoading || contactCandidateMutation.isLoading,
  };
};
